import { put, select, call, takeLatest } from 'redux-saga/effects';
import {
  findSwapReinvestmentAdminSetting,
  findSwapReinvestmentUserSetting,
  getSwapReinvestmentDefaultRateLimit,
} from '../../utils/swapReinvestmentUtils';
import {
  createSwapReinvestmentUserSetting,
  getSwapReinvestmentUserSetting,
  updateSwapReinvestmentUserSetting,
} from '../../api/swapReinvestmentApi';
import {
  changeUserSettingLoading,
  createSwapInvestmentUserSettingSuccess,
  getSwapInvestmentUserSettingsSuccess,
  resetSwapInvestmentUserInput,
  updateSwapInvestmentUserSettingSuccess,
} from '../actions/swapReinvestment';
import { errorHandler } from './errorSaga';
import {
  SR_GET_USER_SETTINGS_REQUEST,
  SR_INITIALIZE_USER_INPUT,
  SR_PATCH_USER_SETTING_REQUEST,
  SR_POST_USER_SETTING_REQUEST,
} from '../actionConstants/swapReinvestmentConstants';
import { DEFAULT_ORDER_FREQUENCY_VALUE } from '../../constants/swapReinvestment';
import { makeAccountInfo } from '../../utils';
import { FX } from '../../constants';

function* getAccountInfo() {
  const maintenanceState = yield select((state) => state.settings.maintenanceInfo);
  const accountState = yield select((state) => state.settings.accountInfo);
  const loginValues = yield select((state) => state.auth.loginValues);
  return makeAccountInfo({ maintenanceState, accountState, loginValues });
}

function* initializeSwapReinvestmentInputHandler(action) {
  const {
    payload: { instrumentId, side, successCallback, failureCallback },
  } = action;
  try {
    const { userSettings, isLoadingUserSettings } = yield select((state) => state.swapReinvestment);
    if (isLoadingUserSettings) {
      return;
    }
    yield put(changeUserSettingLoading({ loading: true })); // 取得中も処理中も同じフラグで良いか
    const instrumentList = yield select((state) => state.settings.instrumentList);
    const created = findSwapReinvestmentUserSetting({ instrumentId, side, userSettings, isLoadingUserSettings });
    const adminSetting = findSwapReinvestmentAdminSetting({ instrumentId, side, instrumentList });
    // 作成済みの場合
    if (created) {
      // 再取得
      const { data } = yield call(getSwapReinvestmentUserSetting, FX);
      yield put(getSwapInvestmentUserSettingsSuccess({ userSettings: data }));
      const userSetting = findSwapReinvestmentUserSetting({
        instrumentId,
        side,
        userSettings: data,
        isLoadingUserSettings,
      });
      if (!userSetting) {
        throw Error('swapReinvestment userSetting not found.');
      }
      // 取得した内容をInputにする
      yield put(resetSwapInvestmentUserInput({ input: userSetting }));
    } else if (adminSetting) {
      // 未作成の場合
      const accountInfo = yield* getAccountInfo();
      const rates = yield select((state) => state.currencies.rates);
      const { defaultRateLimit } =
        getSwapReinvestmentDefaultRateLimit({ instrumentId, side, rates, accountInfo }) || {};
      if (!defaultRateLimit) {
        throw Error('swapReinvestment getEod error.');
      }
      const input = {
        status: true,
        instrumentId,
        side,
        quantity: adminSetting.quantityDefault,
        amount: null,
        orderFrequency: DEFAULT_ORDER_FREQUENCY_VALUE,
        rateLimit: defaultRateLimit,
        quantityLimit: 10, // TODO 確認
      };
      yield put(resetSwapInvestmentUserInput({ input }));
    }
    successCallback?.({ created: !!created });
  } catch (e) {
    yield call(errorHandler, { error: e });
    try {
      failureCallback?.();
    } catch (e2) {
      yield call(errorHandler, { error: e2 });
    }
  } finally {
    yield put(changeUserSettingLoading({ loading: false }));
  }
}

function* createUserSettingsRequestHandler(action) {
  const {
    payload: { successCallback, failureCallback, ...requestBody },
  } = action;
  try {
    const { isLoadingUserSettings } = yield select((state) => state.swapReinvestment);
    if (isLoadingUserSettings) {
      return;
    }
    yield put(changeUserSettingLoading({ loading: true })); // 取得中も処理中も同じフラグで良いか
    const { data } = yield call(createSwapReinvestmentUserSetting, FX, requestBody);
    yield put(createSwapInvestmentUserSettingSuccess({ userSettings: data }));
    successCallback?.();
  } catch (e) {
    yield call(errorHandler, { error: e });
    try {
      failureCallback?.();
    } catch (e2) {
      yield call(errorHandler, { error: e2 });
    }
  } finally {
    yield put(changeUserSettingLoading({ loading: false }));
  }
}

function* updateUserSettingsRequestHandler(action) {
  const {
    payload: { successCallback, failureCallback, ...requestBody },
  } = action;
  try {
    const { isLoadingUserSettings } = yield select((state) => state.swapReinvestment);
    if (isLoadingUserSettings) {
      return;
    }
    yield put(changeUserSettingLoading({ loading: true })); // 取得中も処理中も同じフラグで良いか
    const { data } = yield call(updateSwapReinvestmentUserSetting, FX, requestBody);
    yield put(updateSwapInvestmentUserSettingSuccess({ userSettings: data }));
    successCallback?.();
  } catch (e) {
    yield call(errorHandler, { error: e });
    try {
      failureCallback?.();
    } catch (e2) {
      yield call(errorHandler, { error: e2 });
    }
  } finally {
    yield put(changeUserSettingLoading({ loading: false }));
  }
}

function* getUserSettingsRequestHandler() {
  try {
    yield put(changeUserSettingLoading({ loading: true }));
    const { data } = yield call(getSwapReinvestmentUserSetting, FX);
    yield put(getSwapInvestmentUserSettingsSuccess({ userSettings: data }));
  } catch (e) {
    yield call(errorHandler, { error: e });
  } finally {
    yield put(changeUserSettingLoading({ loading: false }));
  }
}

export default function* swapReinvestmentSagaHandler() {
  yield takeLatest(SR_INITIALIZE_USER_INPUT, initializeSwapReinvestmentInputHandler);
  yield takeLatest(SR_GET_USER_SETTINGS_REQUEST, getUserSettingsRequestHandler);
  yield takeLatest(SR_POST_USER_SETTING_REQUEST, createUserSettingsRequestHandler);
  yield takeLatest(SR_PATCH_USER_SETTING_REQUEST, updateUserSettingsRequestHandler);
}
